import { groupBy } from "lodash";
import { AlertCircle } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { z } from "zod";

import { type DraftPayment, DraftPaymentStatus } from "@/apis/gql/types";
import {
  ValidationErrorMessage,
  ValidationRoot,
  useValidation,
} from "@/components/shared/form/shared/form-validation";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { LoadingSpinner } from "@/components/ui/spinner";
import type { CreatePaymentInput } from "@/queries/payments/create-payment";

import {
  TypographyCaption,
  TypographyH3,
  TypographyLarge,
} from "@/components/ui/typography";
import { cn, formatAmount, formatIban } from "@/lib/utils";
import { paymentSchema } from "../validations/payment";

const MAX_PURPOSE_LENGTH = 140;

type Props = {
  draftPayments: DraftPayment[];
  onConfirm: (
    payments: {
      draftPaymentIds: string[];
      input: Partial<CreatePaymentInput>;
    }[],
  ) => void;
};

export const PaymentReview = ({
  draftPayments: _draftPayments,
  onConfirm,
}: Props) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const payments = _draftPayments.filter(
    (payment) =>
      payment.status &&
      ![DraftPaymentStatus.INITIATED].includes(payment.status),
  );

  const valid = payments.filter((payment) => {
    const result = paymentSchema.safeParse(payment);
    return result.success;
  }) as (DraftPayment & z.infer<typeof paymentSchema>)[];

  const groupedPayments = useMemo(() => {
    const groupedPayments = groupBy(
      valid,
      (payment) => `${payment.recipientIban}_${payment.currency}`,
    );
    return groupedPayments;
  }, [valid]);

  const numberOfPayments = Object.keys(groupedPayments).length;

  const totalPayment = valid.reduce(
    (acc, payment) => acc + (payment.amount ?? 0),
    0,
  );

  /* TODO: handle multiple currencies */
  const paymentCurrency = valid?.[0]?.currency;

  const allHaveSameExecutionDate = valid.every((p) => {
    const pDate = p.executionDate ? new Date(p.executionDate) : null;
    const firstDate = valid[0].executionDate
      ? new Date(valid[0].executionDate)
      : null;
    return pDate?.getTime() === firstDate?.getTime();
  });
  const finalExecutionDate = findEarliestExecutionDate(valid) ?? new Date();

  const invalid = payments.filter((payment) => {
    const result = paymentSchema.safeParse(payment);
    return !result.success;
  });

  const handleConfirm = useCallback(async () => {
    setLoading(true);

    try {
      const targetExecutionDate: Date | null = findEarliestExecutionDate(valid);

      const transactions = Object.values(groupedPayments).map((payments) => {
        const payment = payments[0];

        const totalAmount = payments.reduce(
          (acc, p) => acc + (p.amount ?? 0),
          0,
        );

        const _purpose = payments.map((p) => p.fullPurpose).join(", ");

        const purpose =
          _purpose.length >= MAX_PURPOSE_LENGTH
            ? t("payments.purpose_truncated", { count: payments.length })
            : _purpose;

        const draftPayment: Omit<
          DraftPayment,
          "draftPaymentId" | "tenantId" | "createdAt" | "status" | "fullPurpose"
        > = {
          executionDate: targetExecutionDate ?? new Date(),
          amount: totalAmount,
          currency: payment.currency,
          purpose: purpose,
          recipientIban: payment.recipientIban,
          recipientName: payment.recipientName,
          invoices: payments.map((p) => p.invoices[0]),
          rawAmount: null, // FIX
          discount: null, // FIX
        };

        return {
          draftPaymentIds: payments.map((p) => p.draftPaymentId),
          input: draftPayment,
        };
      });

      await onConfirm(transactions);
    } finally {
      setLoading(false);
    }
  }, [groupedPayments, valid, onConfirm]);

  return (
    <div className="container max-w-4xl mx-auto">
      <div className="flex flex-col gap-4">
        <div className="flex items-center">
          <div className="flex items-center gap-2">
            <TypographyH3>{t("payment_steps.step_1.title")}</TypographyH3>
            <div
              className={cn(
                "w-8 h-8 rounded-full border bg-slate-100 flex items-center justify-center font-semibold",
              )}
            >
              {numberOfPayments}
            </div>
          </div>

          <div className="flex flex-col items-center ml-auto mr-6">
            <TypographyCaption>{t("execution_date")}</TypographyCaption>
            <TypographyLarge>
              {new Date(finalExecutionDate).toLocaleDateString("de-DE")}
            </TypographyLarge>
          </div>

          {paymentCurrency && (
            <div className="flex flex-col items-center">
              <TypographyCaption>{t("total_amount")}</TypographyCaption>
              <TypographyLarge>
                {formatAmount(totalPayment, paymentCurrency)}
              </TypographyLarge>
            </div>
          )}
        </div>
        {!allHaveSameExecutionDate && (
          <Alert variant="warning">
            <AlertTitle>{t("payments.execution_date_warning")}</AlertTitle>
            <AlertDescription>
              {t("payments.execution_date_warning_description")}
            </AlertDescription>
          </Alert>
        )}

        <div className="flex flex-col gap-4">
          {Object.values(groupedPayments).map((payment) => {
            const recipientIban = payment[0].recipientIban;
            const recipientName = payment[0].recipientName;

            const totalAmount = payment.reduce(
              (acc, p) => acc + (p.amount ?? 0),
              0,
            );

            const currency = payment[0].currency;

            return (
              <_GroupedPaymentReviewCard
                key={recipientIban}
                supplier={recipientName ?? ""}
                totalAmount={totalAmount}
                iban={recipientIban}
                currency={currency}
                payments={payment}
              />
            );
          })}
        </div>
        <div className="flex flex-col items-center">
          <Button
            onClick={handleConfirm}
            disabled={numberOfPayments === 0 || loading}
            className="gap-2"
          >
            {loading && <LoadingSpinner />}
            {`${t("confirm_and_continue")} (${numberOfPayments})`}
          </Button>
        </div>
        {invalid.length > 0 && (
          <>
            <h2 className="text-xl font-semibold mb-4 mt-10">
              Ungültige Zahlungen ({invalid.length})
              <span className="text-sm font-normal text-gray-500 ml-2">
                Diese werden nicht verarbeitet
              </span>
            </h2>
            <div className="flex flex-col gap-4">
              {invalid.map((payment) => {
                return (
                  <PaymentReviewCard
                    key={payment.draftPaymentId}
                    supplier={payment.recipientName ?? ""}
                    invoiceNumber={payment.invoices[0].invoiceNo}
                    totalAmount={payment.invoices[0].totalAmount}
                    iban={payment.recipientIban ?? ""}
                    purpose={payment.fullPurpose}
                    paymentAmount={payment.amount ?? 0}
                    currency={payment.currency ?? ""}
                  />
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

interface PaymentReviewCardProps {
  supplier: string;
  invoiceNumber: string;
  totalAmount: number;
  iban: string;
  paymentAmount: number;
  currency: string;
  purpose: string | null;
}

const PaymentReviewCard: React.FC<PaymentReviewCardProps> = (props) => {
  const { iban, currency, paymentAmount, purpose } = props;

  return (
    <ValidationRoot
      schema={paymentSchema}
      values={{
        recipientIban: iban,
        amount: paymentAmount,
        currency,
        purpose,
      }}
    >
      <_PaymentReviewCard {...props} />
    </ValidationRoot>
  );
};

const _PaymentReviewCard: React.FC<PaymentReviewCardProps> = ({
  supplier,
  invoiceNumber,
  totalAmount,
  iban,
  paymentAmount,
  purpose,
  currency,
}) => {
  const { t } = useTranslation();
  const { isValid } = useValidation();

  return (
    <div
      className={cn(
        "rounded-xl border overflow-hidden shadow-sm",
        isValid ? "border-gray-200" : "border-red-300",
      )}
    >
      {!isValid && (
        <div className="px-4 py-2 flex items-center justify-between bg-red-50">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
            <span className="text-sm font-medium text-red-700">
              {t("payment_error.title")}
            </span>
          </div>
        </div>
      )}
      <div className="bg-gray-100 px-6 py-4 grid grid-cols-3 gap-4">
        <div>
          <h3 className="text-sm text-gray-500 font-medium">Lieferant</h3>
          <p className="text-md font-semibold mt-1">{supplier}</p>
        </div>
        <div className="text-center">
          <h3 className="text-sm text-gray-500 font-medium">Rechnungsnummer</h3>
          <p className="text-md font-semibold mt-1">{invoiceNumber}</p>
        </div>
        <div className="text-right">
          <h3 className="text-sm text-gray-500 font-medium">
            {t("payment_review.total_invoice_amount")}
          </h3>
          <p className="text-md font-semibold mt-1">
            {formatAmount(totalAmount, currency)}
          </p>
          <ValidationErrorMessage name="currency" />
          <ValidationErrorMessage name="totalAmount" />
        </div>
      </div>
      <div className="bg-white px-6 py-6 grid grid-cols-3 gap-4">
        <div>
          <h3 className="text-sm text-gray-500 font-medium">IBAN</h3>
          <p className="text-md font-semibold mt-1">{formatIban(iban)}</p>
          <ValidationErrorMessage name="recipientIban" />
        </div>
        <div className="text-center">
          <h3 className="text-sm text-gray-500 font-medium">
            Zahlungsreferenz
          </h3>
          <p className="text-md font-semibold mt-1">{purpose}</p>
          <ValidationErrorMessage name="purpose" />
        </div>
        <div className="text-right">
          <h3 className="text-sm text-gray-500 font-medium">Zahlungsbetrag</h3>
          <p className="text-md font-semibold mt-1">
            {formatAmount(paymentAmount, currency)}
          </p>
          <ValidationErrorMessage name="amount" />
        </div>
      </div>
    </div>
  );
};

const _GroupedPaymentReviewCard: React.FC<{
  supplier: string;
  totalAmount: number;
  iban: string;
  currency: string;
  payments: DraftPayment[];
}> = ({ supplier, totalAmount, iban, currency, payments }) => {
  const { t } = useTranslation();
  const { isValid } = useValidation();

  return (
    <div
      className={cn(
        "rounded-xl border overflow-hidden shadow-sm",
        isValid ? "border-gray-200" : "border-red-300",
      )}
    >
      {!isValid && (
        <div className="px-4 py-2 flex items-center justify-between bg-red-50">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
            <span className="text-sm font-medium text-red-700">
              {t("payment_error.title")}
            </span>
          </div>
        </div>
      )}
      <div className="bg-gray-100 px-6 py-4 grid grid-cols-3 gap-4">
        <div>
          <h3 className="text-sm text-gray-500 font-medium">Lieferant</h3>
          <p className="text-md font-semibold mt-1">{supplier}</p>
        </div>
        <div className="text-center">
          <h3 className="text-sm text-gray-500 font-medium">IBAN</h3>
          <p className="text-md font-semibold mt-1">{formatIban(iban)}</p>
          <ValidationErrorMessage name="recipientIban" />
        </div>
        <div className="text-right">
          <h3 className="text-sm text-gray-500 font-medium">
            {t("payment_review.total_supplier_amount")}
          </h3>
          <p className="text-md font-semibold mt-1">
            {formatAmount(totalAmount, currency)}
          </p>
          <ValidationErrorMessage name="currency" />
          <ValidationErrorMessage name="totalAmount" />
        </div>
      </div>
      <div className="bg-gray-100 px-6 pb-6 flex flex-col gap-2">
        {payments.map((payment) => (
          <div
            key={payment.draftPaymentId}
            className="bg-white px-6 py-6 border rounded-xl flex flex-col gap-3"
          >
            <div className="grid grid-cols-2 gap-4">
              <div>
                <h3 className="text-sm text-gray-500 font-medium">
                  Rechnungsnummer
                </h3>
                <p className="text-md font-semibold mt-1">
                  {payment.invoices[0].invoiceNo}
                </p>
              </div>
              <div className="text-right">
                <h3 className="text-sm text-gray-500 font-medium">
                  {t("payment_review.total_invoice_amount")}
                </h3>
                <p className="text-md font-semibold mt-1">
                  {formatAmount(payment.invoices[0].totalAmount, currency)}
                </p>
                <ValidationErrorMessage name="currency" />
                <ValidationErrorMessage name="totalAmount" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <h3 className="text-sm text-gray-500 font-medium">
                  Zahlungsreferenz
                </h3>
                <p className="text-md font-semibold mt-1">
                  {payment.fullPurpose}
                </p>
                <ValidationErrorMessage name="purpose" />
              </div>
              <div className="text-right">
                <h3 className="text-sm text-gray-500 font-medium">
                  Zahlungsbetrag
                </h3>
                <p className="text-md font-semibold mt-1">
                  {formatAmount(payment.amount ?? 0, currency)}
                </p>
                <ValidationErrorMessage name="amount" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export function findEarliestExecutionDate(draftPayments: DraftPayment[]) {
  const hasNullExecutionDate = draftPayments.some(
    (payment) => payment.executionDate === null,
  );

  let targetExecutionDate: Date | null = null;

  if (!hasNullExecutionDate) {
    const validExecutionDates = draftPayments
      .map((payment) =>
        payment.executionDate ? new Date(payment.executionDate) : null,
      )
      .filter((date): date is Date => date !== null);

    if (validExecutionDates.length > 0) {
      targetExecutionDate = new Date(
        Math.min(...validExecutionDates.map((date) => date.getTime())),
      );
    }
  }
  return targetExecutionDate;
}
